import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { XCircle, AlertTriangle, Ban } from 'lucide-react';

const WhatDidntWorkSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Approaches that DeepSeek tried and abandoned during R1 development"
      >
        What Didn't Work
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* MCTS */}
          <div className="slide-card border-destructive/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 border border-destructive/20 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground">Monte Carlo Tree Search</h3>
                <p className="text-xs text-destructive">Failed Approach</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-destructive/10">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-destructive">Problem:</strong> Token generation creates exponentially larger search spaces than games
                </p>
              </div>
              
              <ul className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <Ban className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                  <span>Value model training remains challenging</span>
                </li>
                <li className="flex items-start gap-2">
                  <Ban className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                  <span>Branching factor too high for LLM outputs</span>
                </li>
                <li className="flex items-start gap-2">
                  <Ban className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                  <span>Compute overhead not justified by gains</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* PRMs */}
          <div className="slide-card border-chart-4/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg" style={{ background: 'hsla(45, 93%, 58%, 0.1)', border: '1px solid hsla(45, 93%, 58%, 0.2)' }}>
                <div className="w-full h-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" style={{ color: 'hsl(var(--chart-4))' }} />
                </div>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground">Process Reward Models</h3>
                <p className="text-xs" style={{ color: 'hsl(var(--chart-4))' }}>Limited Benefit</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 rounded-lg" style={{ background: 'hsla(45, 93%, 58%, 0.1)' }}>
                <p className="text-sm text-muted-foreground">
                  <strong style={{ color: 'hsl(var(--chart-4))' }}>Why DeepSeek avoided PRMs:</strong>
                </p>
              </div>
              
              <ul className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <span style={{ color: 'hsl(var(--chart-4))' }}>1.</span>
                  <span>Difficulty defining fine-grained steps in general reasoning</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'hsl(var(--chart-4))' }}>2.</span>
                  <span>Challenge determining intermediate step correctness</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: 'hsl(var(--chart-4))' }}>3.</span>
                  <span>Susceptibility to reward hacking</span>
                </li>
              </ul>
              
              <p className="text-xs text-muted-foreground p-2 rounded bg-muted/50">
                OpenAI showed PRMs help (78.2% vs ORMs on MATH), but overhead wasn't worth it at scale.
              </p>
            </div>
          </div>
          
          {/* R1-Zero Issues */}
          <div className="slide-card border-secondary/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground">R1-Zero Limitations</h3>
                <p className="text-xs text-secondary">Required Pipeline Fix</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-secondary/10">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-secondary">Issues with pure RL:</strong>
                </p>
              </div>
              
              <ul className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                  <span><strong className="text-foreground">Poor readability</strong> - Outputs hard to follow</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                  <span><strong className="text-foreground">Language mixing</strong> - Chinese-English switches</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                  <span><strong className="text-foreground">Weak non-reasoning</strong> - Writing, QA suffered</span>
                </li>
              </ul>
              
              <div className="p-2 rounded bg-accent/10 border border-accent/20">
                <p className="text-xs text-accent">
                  ✓ Fixed by 4-stage pipeline with cold-start SFT
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 p-4 rounded-xl bg-muted/30 border border-border">
          <p className="text-sm text-center text-muted-foreground">
            <strong className="text-foreground">Lesson:</strong> Pure RL proves reasoning emergence, but production models need hybrid approaches combining RL with targeted SFT.
          </p>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default WhatDidntWorkSlide;
